import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, MessageCircle } from "lucide-react";
import XLogo from "./XLogo";
import { WA_DEFAULT, scrollToId } from "@/data/content";

const LINKS = [
  { id: "showcase", label: "Showcase", testid: "nav-link-showcase" },
  { id: "about", label: "About", testid: "nav-link-about" },
  { id: "services", label: "Services", testid: "nav-link-services" },
  { id: "terminal", label: "Terminal", testid: "nav-link-terminal" },
  { id: "risk", label: "Risk", testid: "nav-link-risk" },
  { id: "booking", label: "Booking", testid: "nav-link-booking" },
  { id: "advisory", label: "Advisory", testid: "nav-link-advisory" },
];

export default function Navbar({ theme, onToggleTheme }) {
  const [open, setOpen] = useState(false);

  return (
    <header className="glass fixed inset-x-0 top-9 z-[100] border-b border-line">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <button
          data-testid="header-brand-logo"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-2.5"
        >
          <XLogo size={30} />
          <span className="font-display text-2xl font-black uppercase tracking-[0.18em] text-ink">
            Team<span className="text-brand">&nbsp;X</span>
          </span>
        </button>

        <div className="hidden items-center gap-6 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollToId(l.id)}
              className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-mute transition-colors duration-200 hover:text-brand"
            >
              {l.label}
            </button>
          ))}
          <Link
            data-testid="nav-link-insights"
            to="/insights"
            className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-mute transition-colors duration-200 hover:text-brand"
          >
            Insights
          </Link>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            data-testid="theme-toggle-button"
            onClick={onToggleTheme}
            aria-label="Toggle dark / light mode"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-mute transition-colors duration-200 hover:border-brand hover:text-brand"
          >
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <a
            data-testid="header-whatsapp-cta"
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full bg-brand px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-brand-crimson sm:inline-flex"
          >
            <MessageCircle size={14} />
            WhatsApp
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen((o) => !o)}
            aria-label="Open menu"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink lg:hidden"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="glass overflow-hidden border-t border-line lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => {
                    setOpen(false);
                    scrollToId(l.id);
                  }}
                  className="py-2.5 text-left font-mono text-xs uppercase tracking-[0.25em] text-mute transition-colors hover:text-brand"
                >
                  {l.label}
                </button>
              ))}
              <Link
                data-testid="mobile-nav-link-insights"
                to="/insights"
                onClick={() => setOpen(false)}
                className="py-2.5 text-left font-mono text-xs uppercase tracking-[0.25em] text-mute transition-colors hover:text-brand"
              >
                Insights
              </Link>
              <a
                data-testid="mobile-header-whatsapp-cta"
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-white"
              >
                <MessageCircle size={14} />
                WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
